<template>
  <button
    :class="{
      'appearance-primary': appearance === 'primary',
      'appearance-secondary': appearance === 'secondary',
      'appearance-square': appearance === 'square',
      'appearance-menu': appearance === 'menu',
      'size-large': size === 'large',
      'size-medium': size === 'medium',
      'size-small': size === 'small',
      'size-mini': size === 'mini',
      'size-square': size === 'square',
      'text-center': textAlignment === 'center',
      'text-left': textAlignment === 'left',
      'text-right': textAlignment === 'right'
    }"
    :type="type"
    :disabled="!isDisabled"
    @click="$emit('click', action)"
    :aria-label="ariaLabel"
    :id="id"
  >
    <span v-if="prefix" class="prefix">
      <doe-icon :icon="prefix" :size="iconSize" :colour="iconColour" />
    </span>
    <span :class="{ 'pl-1': hasSlot && prefix, 'pr-1': hasSlot && suffix }">
      {{ buttonLabel }}
    </span>
    <span v-if="suffix" class="suffix">
      <doe-icon :icon="suffix" :size="iconSize" />
    </span>
  </button>
</template>

<script>
import dynamicValues from "@/mixins/dynamicValues";
import DoeIcon from "@/components/icon/Icon";

export default {
  name: "DoeButton",
  mixins: [dynamicValues],
  props: {
    disabled: {
      type: Boolean,
      default: true
    },
    skippable: {
      type: Boolean,
      default: false
    },
    label: {
      required: false
    },
    id: {
      type: String
    },
    ariaLabel: {
      type: String,
      required: false
    },
    appearance: {
      type: String,
      default: "default",
      validator: value => {
        return (
          ["default", "menu", "primary", "secondary", "square"].indexOf(
            value
          ) !== -1
        );
      }
    },
    action: {
      type: String
    },
    size: {
      type: String,
      default: "small",
      validator: value => {
        return ["large", "medium", "small", "mini", "modal"].indexOf(value?value:"") !== -1;
      }
    },
    type: {
      type: String,
      default: "button",
      validator: value => {
        return ["button", "reset", "submit"].indexOf(value) !== -1;
      }
    },
    prefix: {
      type: [Boolean, String],
      default: false
    },
    suffix: {
      type: [Boolean, String],
      default: false
    },
    iconColour: {
      type: String
    },
    iconSize: {
      type: String,
      default: "medium",
      validator: value => {
        return ["large", "medium", "small", "mini"].indexOf(value) !== -1;
      }
    },
    textAlignment: {
      type: String,
      default: "center"
    }
  },
  components: {
    DoeIcon
  },
  methods: {
    hasSlot() {
      return !!this.$slots.default;
    }
  },
  computed: {
    buttonLabel() {
      if (this.hasSlot()) {
        return this.getLabelValue(this.$slots.default[0].text);
      } else {
        return this.getLabelValue(this.label);
      }
    },
    isDisabled() {
      if(this.skippable) {
        return this.skippable;
      } else {
        return this.disabled;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
span {
  @apply align-middle;
}
.prefix,
.suffix {
  @apply inline-block;
}
button {
  @apply cursor-pointer text-blue-900 font-sans font-semibold select-none w-full;
  svg {
    @apply fill-current;
  }
  @screen md {
    @apply w-auto;
  }
}
.appearance-primary,
.appearance-secondary {
  @apply py-2 px-3 rounded border-2;
}
.appearance-primary {
  @apply text-white bg-red-900 border-red-900;
  &:focus {
    outline: 1px solid #ce0037;
    outline-offset: 3px;
  }
}
.appearance-secondary {
  @apply text-blue-900 bg-gray-400 border-blue-900;
  &:focus {
    outline: 1px solid #041e42;
    outline-offset: 3px;
  }
}

.appearance-primary:disabled,
.appearance-primary[disabled] {
  @apply cursor-not-allowed bg-red-800 border-red-800;
}
.appearance-secondary:disabled,
.appearance-secondary[disabled] {
  @apply cursor-not-allowed text-gray-700 bg-gray-400 border-gray-700;
}

.appearance-square {
  @apply px-0 min-w-auto text-center;
  height: 44px;
  width: 44px;
}
.appearance-menu {
  @apply font-light px-0 min-w-auto;
  height: 44px;
}
.size-large {
  @apply leading-snug min-w-lg py-3 text-2xl;
}
.size-medium {
  @apply leading-snug min-w-lg py-2 text-xl;
}
.size-small {
  @apply leading-snug py-2 min-w-sm text-base;
}
.size-modal {
  @apply leading-snug py-2 text-base;
}
.size-mini {
  @apply text-left leading-snug w-auto py-3 text-base;
  max-height: 44px;
}
.appearance-square.size-small {
  @apply min-w-auto;
}
</style>
